/* eslint-disable @typescript-eslint/no-use-before-define, no-plusplus */

import { BookmarkConfig } from '../../../../../../../../assets-source/scripts/bookmarks/bookmarks';
import { isMobileDevice } from './utils';

declare let toggleBookmarks: (event: any, config: BookmarkConfig) => void;

type WorksheetCardProps = {
  card: Element;
};

export class WorksheetCard {
  private static readonly galleryBlockClassName = 'gallery';

  private static readonly controllerWrapperClassName = 'gallery__controls-wrapper';

  private static readonly controllerClassName = 'gallery__control';

  private static readonly activeControllerClassName = 'gallery__control--active';

  private static readonly imageSegmentClassName = 'gallery__control-col';

  private static readonly sliderTrackClassName = 'gallery__img-list';

  private static readonly imageClassName = 'gallery__img';

  private static readonly visibleImageClassName = 'gallery__img--visible';

  private gallery: HTMLElement;

  private readonly card: Element;

  private readonly gallerySegments: HTMLCollectionOf<HTMLElement>;

  private readonly galleryImages: HTMLCollectionOf<HTMLElement>;

  constructor(props: WorksheetCardProps) {
    this.card = props.card;

    this.gallery = this.card.getElementsByClassName(WorksheetCard.galleryBlockClassName)[0] as HTMLElement;

    // eslint-disable-next-line max-len
    this.gallerySegments = this.card.getElementsByClassName(WorksheetCard.imageSegmentClassName) as HTMLCollectionOf<HTMLElement>;
    // eslint-disable-next-line max-len
    this.galleryImages = this.gallery.getElementsByClassName(WorksheetCard.imageClassName) as HTMLCollectionOf<HTMLElement>;
  }

  get galleryImagesCount() {
    return this.galleryImages.length;
  }

  get gallerySegmentsCount() {
    return this.gallerySegments.length;
  }

  init = () => {
    this.initGallery();
    this.initFavorites();
  };

  initFavorites = () => {
    const cardFavoritesButton = this.card.getElementsByClassName('gallery__favorite')[0];

    cardFavoritesButton.addEventListener('click', (event) => {
      toggleBookmarks(event, { isDocument: true });
    });
  };

  initGallery = () => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < this.galleryImagesCount; i++) {
      if (i) {
        this.galleryImages[i].classList.remove(WorksheetCard.visibleImageClassName);
      }
    }

    if (isMobileDevice()) {
      this.addGalleryTouchEvents();
    } else {
      this.setGalleryMouseLeaveHandler();
      this.setGalleryControllersMouseEnterHandlers();
    }
  };

  setGalleryMouseLeaveHandler = () => {
    this.gallery.addEventListener('mouseleave', () => {
      for (let i = 0; i < this.galleryImagesCount; i++) {
        this.galleryImages[i].classList.remove(WorksheetCard.visibleImageClassName);
        this.gallerySegments[i]
          .getElementsByClassName(WorksheetCard.controllerClassName)[0]
          .classList
          .remove(WorksheetCard.activeControllerClassName);
      }

      this.galleryImages[0].classList.add(WorksheetCard.visibleImageClassName);
      this.gallerySegments[0]
        .getElementsByClassName(WorksheetCard.controllerClassName)[0]
        .classList
        .add(WorksheetCard.activeControllerClassName);
    });
  };

  setGalleryControllersMouseEnterHandlers = () => {
    for (let i = 0; i < this.gallerySegmentsCount; i++) {
      this.gallerySegments[i].addEventListener('mouseenter', () => {
        this.handleMouseEnterGalleryController(this.gallerySegments[i], i);
      });
    }
  };

  handleMouseEnterGalleryController = (segment: HTMLElement, index: number) => {
    for (let i = 0; i < this.gallerySegmentsCount; i++) {
      this.gallerySegments[i]
        .getElementsByClassName(WorksheetCard.controllerClassName)[0]
        .classList
        .remove(WorksheetCard.activeControllerClassName);
    }

    const controller = segment.querySelector(`.${WorksheetCard.controllerClassName}`);

    controller.classList.add(WorksheetCard.activeControllerClassName);

    for (let i = 0; i < this.galleryImagesCount; i++) {
      this.galleryImages[i].classList.remove(WorksheetCard.visibleImageClassName);
    }

    this.galleryImages[index].classList.add(WorksheetCard.visibleImageClassName);

    WorksheetCard.replacePlaceholderWithImage(this.galleryImages[index]);
  };

  addGalleryTouchEvents = () => {
    const slider = this.gallery.getElementsByClassName(WorksheetCard.controllerWrapperClassName)[0] as HTMLElement;
    const sliderTrack = this.gallery.getElementsByClassName(WorksheetCard.sliderTrackClassName)[0] as HTMLElement;
    const galleryPoints = this.gallery.getElementsByClassName(WorksheetCard.controllerClassName);
    const slides = this.galleryImages;
    const slideWidth = slides[0].offsetWidth;

    const posThreshold = slides[0].offsetWidth * 0.35;
    const lastTrf = (slides.length - 1) * slideWidth;
    const trfRegExp = /([-0-9.]+(?=px))/;
    let posInit = 0;
    let posX1 = 0;
    let posX2 = 0;
    let posFinal = 0;
    let slideIndex = 0;
    let nextTrf = 0;
    let prevTrf = 0;
    let allowSwipe = true;
    let transition = true;

    slider.addEventListener('touchstart', (event) => {
      const touchData = event.touches[0];

      if (allowSwipe) {
        transition = true;
        nextTrf = (slideIndex + 1) * -slideWidth;
        prevTrf = (slideIndex - 1) * -slideWidth;
        posX1 = touchData.clientX;
        posInit = posX1;

        sliderTrack.style.transition = '';

        document.addEventListener('touchmove', swipeAction);
        document.addEventListener('touchend', swipeEnd);
      }
    });

    sliderTrack.style.transform = 'translate3d(0px, 0px, 0px)';

    sliderTrack.addEventListener('transitionend', () => {
      allowSwipe = true;
    });

    function slide() {
      if (transition) {
        sliderTrack.style.transition = 'transform .5s';
      }

      sliderTrack.style.transform = `translate3d(-${slideIndex * slideWidth}px, 0px, 0px)`;
    }

    function swipeAction(event) {
      const evt = event.touches[0];
      const style = sliderTrack.style.transform;
      const transform = +style.match(trfRegExp)[0];

      posX2 = posX1 - evt.clientX;
      posX1 = evt.clientX;

      if (slideIndex === 0) {
        if (posInit < posX1) {
          setTransform(transform, 0);
          return;
        }

        allowSwipe = true;
      }

      let slideLength = slides.length;

      if (slideIndex === --slideLength) {
        if (posInit > posX1) {
          setTransform(transform, lastTrf);
          return;
        }

        allowSwipe = true;
      }

      if ((posInit > posX1 && transform < nextTrf) || (posInit < posX1 && transform > prevTrf)) {
        reachEdge();
        return;
      }

      if (slides[slideIndex + 1]) {
        WorksheetCard.replacePlaceholderWithImage(slides[slideIndex + 1]);
      }

      sliderTrack.style.transform = `translate3d(${transform - posX2}px, 0px, 0px)`;
    }

    function swipeEnd() {
      posFinal = posInit - posX1;

      document.removeEventListener('touchmove', swipeAction);
      document.removeEventListener('mousemove', swipeAction);
      document.removeEventListener('touchend', swipeEnd);
      document.removeEventListener('mouseup', swipeEnd);

      if (allowSwipe) {
        if (Math.abs(posFinal) > posThreshold) {
          if (posInit < posX1) {
            slideIndex--;
          } else if (posInit > posX1) {
            slideIndex++;
          }
        }

        if (posInit !== posX1) {
          allowSwipe = false;
          slide();
        } else {
          allowSwipe = true;
        }
      } else {
        allowSwipe = true;
      }

      for (let i = 0; i < galleryPoints.length; i++) {
        galleryPoints[i].classList.remove(WorksheetCard.activeControllerClassName);
      }

      galleryPoints[slideIndex].classList.add(WorksheetCard.activeControllerClassName);
    }

    function setTransform(transform, compareTransform) {
      if (transform > compareTransform) {
        sliderTrack.style.transform = `translate3d(${compareTransform}px, 0px, 0px)`;
      }

      allowSwipe = false;
    }

    function reachEdge() {
      transition = false;
      swipeEnd();
      allowSwipe = true;
    }
  };

  private static replacePlaceholderWithImage = (imageWrapper) => {
    const spanSlide = imageWrapper.getElementsByTagName('SPAN')[0];

    if (spanSlide) {
      const img = new Image();

      img.src = spanSlide.dataset.src;
      img.alt = spanSlide.dataset.alt;

      spanSlide.parentNode.removeChild(spanSlide);
      imageWrapper.appendChild(img);
    }
  };
}
